import React, { useState, useEffect, useRef } from 'react';
import './ConfiguracionSEO.css';
import baseURL from '../../url';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faShareAlt,
    faCode,
    faSave,
    faSync,
    faImage,
    faUpload,
    faCheckCircle,
    faCopy,
    faEye,
    faGlobe,
    faChartLine,
    faRobot,
    faPalette,
    faMobileAlt,
    faBrush,
    faTrash,
    faHeart,
    faLock,
    faPlane,
    faCompass,
    faSuitcaseRolling,
    faPhoneAlt,
    faEnvelope,
    faClock,
    faMapMarkerAlt,
    faCoins,
    faTicketAlt,
    faShieldAlt,
    faStar,
    faToggleOn,
    faToggleOff,
    faDownload,
    faBolt,
    faRocket,
    faLaptop,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { notificarCambioConfiguracion, aplicarConfiguracionEnDOM } from '../../useSiteConfig';

export default function ConfiguracionSEO() {
    const [activeTab, setActiveTab] = useState('colores'); // 'colores' | 'datos_agencia' | 'seo' | 'og' | 'code'
    const [socialPreviewMode, setSocialPreviewMode] = useState('whatsapp'); // 'whatsapp' | 'twitter'
    const [loading, setLoading] = useState(true);
    const [guardando, setGuardando] = useState(false);

    // Archivo de imagen Open Graph local
    const [imagenArchivo, setImagenArchivo] = useState(null);
    const [imagenPreviewLocal, setImagenPreviewLocal] = useState('');
    const fileInputRef = useRef(null);

    // Archivo de Logo del Sitio local (superior y footer)
    const [logoArchivo, setLogoArchivo] = useState(null);
    const [logoPreviewLocal, setLogoPreviewLocal] = useState('');
    const logoInputRef = useRef(null);

    // Archivo de Favicon local
    const [faviconArchivo, setFaviconArchivo] = useState(null);
    const [faviconPreviewLocal, setFaviconPreviewLocal] = useState('');
    const faviconInputRef = useRef(null);

    // Archivo de Fondo del Footer local
    const [footerBgArchivo, setFooterBgArchivo] = useState(null);
    const [footerBgPreviewLocal, setFooterBgPreviewLocal] = useState('');
    const footerBgInputRef = useRef(null);

    // Estado del formulario enfocado 100% en Agencia de Viajes y Turismo
    const [form, setForm] = useState({
        meta_title: 'SEYLIS TOURS - Agencia de Viajes, Paquetes Turísticos y Tours',
        meta_description: 'Descubre los mejores destinos con SEYLIS TOURS. Paquetes turísticos nacionales e internacionales, tours guiados, vuelos, hoteles, excursiones y experiencias inolvidables en Perú y el mundo.',
        meta_keywords: 'agencia de viajes, seylis tours, paquetes turisticos, tours tingo maria, turismo de aventura, vuelos baratos, hoteles todo incluido, excursiones selva, vacaciones familiares',
        meta_author: 'SEYLIS TOURS',
        canonical_url: 'https://tingoaventura.com/',
        site_name: 'SEYLIS TOURS',
        google_verification: '',
        google_analytics_id: '',
        robots_index: 'index, follow',
        og_title: 'SEYLIS TOURS | Agencia de Viajes y Turismo',
        og_description: '¡Planifica tus próximas vacaciones con SEYLIS TOURS! Paquetes turísticos completos, tours guiados, hoteles y vuelos con la mejor tarifa garantizada.',
        og_image: '',
        og_type: 'website',
        og_locale: 'es_PE',
        twitter_card: 'summary_large_image',
        twitter_site: '@seylistours',
        logo: '',
        favicon: '',
        color_menu_movil: '#059669',
        color_footer: '#064E3B',
        footer_bg_image: '',
        footer_bg_opacity: '0.70',
        slogan: 'Tu agencia de confianza, viajes y experiencias inolvidables',
        telefono_reservas: '+51 987 654 321',
        whatsapp_reservas: '51987654321',
        email_reservas: 'reservas@tingoaventura.com',
        direccion_agencia: 'Av. Alameda Perú 450 - Tingo María, Huánuco, Perú',
        horario_atencion: 'Lunes a Sábado: 8:00 AM - 7:30 PM | Domingo: 9:00 AM - 2:00 PM',
        registro_turismo: 'DIRCETUR / MINCETUR Licencia N° 2026-AV',
        moneda_default: 'PEN',
        mensaje_emergencia: 'Asistencia personalizada y soporte al viajero 24/7',
        pwa_activo: '1',
        pwa_nombre: 'SEYLIS TOURS',
        pwa_descripcion: 'Instala y Planifica tus Viajes'
    });

    useEffect(() => {
        cargarConfiguracion();
    }, []);

    const cargarConfiguracion = () => {
        setLoading(true);
        fetch(`${baseURL}/configuracionGet.php`)
            .then(res => res.json())
            .then(data => {
                if (data && data.config) {
                    setForm(prev => ({
                        ...prev,
                        ...data.config
                    }));
                    aplicarConfiguracionEnDOM(data.config);
                }
                setLoading(false);
            })
            .catch(err => {
                console.error('Error al cargar configuración:', err);
                // Si falla el endpoint, cargar desde el JSON público directamente
                fetch('/configuracion_seo.json')
                    .then(res => res.json())
                    .then(jsonData => {
                        setForm(prev => ({ ...prev, ...jsonData }));
                        aplicarConfiguracionEnDOM(jsonData);
                    })
                    .catch(() => {})
                    .finally(() => setLoading(false));
            });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectColor = (campo, color) => {
        setForm(prev => ({ ...prev, [campo]: color }));
    };

    // Manejo de imagen Open Graph
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImagenArchivo(file);
            const previewUrl = URL.createObjectURL(file);
            setImagenPreviewLocal(previewUrl);
        }
    };

    const handleQuitarImagen = () => {
        setImagenArchivo(null);
        setImagenPreviewLocal('');
        setForm(prev => ({ ...prev, og_image: '' }));
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Manejo de Logo del Sitio (superior y footer)
    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoArchivo(file);
            const previewUrl = URL.createObjectURL(file);
            setLogoPreviewLocal(previewUrl);
        }
    };

    const handleQuitarLogo = () => {
        setLogoArchivo(null);
        setLogoPreviewLocal('');
        setForm(prev => ({ ...prev, logo: '' }));
        if (logoInputRef.current) {
            logoInputRef.current.value = '';
        }
    };

    // Manejo de Favicon
    const handleFaviconChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFaviconArchivo(file);
            const previewUrl = URL.createObjectURL(file);
            setFaviconPreviewLocal(previewUrl);
        }
    };

    const handleQuitarFavicon = () => {
        setFaviconArchivo(null);
        setFaviconPreviewLocal('');
        setForm(prev => ({ ...prev, favicon: '' }));
        if (faviconInputRef.current) {
            faviconInputRef.current.value = '';
        }
    };

    // Manejo de Fondo del Footer
    const handleFooterBgChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFooterBgArchivo(file);
            const previewUrl = URL.createObjectURL(file);
            setFooterBgPreviewLocal(previewUrl);
        }
    };

    const handleQuitarFooterBg = () => {
        setFooterBgArchivo(null);
        setFooterBgPreviewLocal('');
        setForm(prev => ({ ...prev, footer_bg_image: '' }));
        if (footerBgInputRef.current) {
            footerBgInputRef.current.value = '';
        }
    };

    const handleCopiarDeSEO = () => {
        setForm(prev => ({
            ...prev,
            og_title: prev.meta_title,
            og_description: prev.meta_description
        }));
        Swal.fire({
            icon: 'info',
            title: 'Campos copiados',
            text: 'Se han sincronizado el título y la descripción de SEO para Open Graph de la agencia.',
            timer: 1500,
            showConfirmButton: false
        });
    };

    const agregarKeyword = (palabra) => {
        const keywordsActuales = form.meta_keywords ? form.meta_keywords.split(',').map(k => k.trim()) : [];
        if (!keywordsActuales.includes(palabra)) {
            const nuevas = [...keywordsActuales, palabra].filter(Boolean).join(', ');
            setForm(prev => ({ ...prev, meta_keywords: nuevas }));
        }
    };

    // Alternar activación de PWA con guardado y sincronización inmediata
    const handleTogglePwaRapido = async () => {
        const nuevoEstado = String(form.pwa_activo) === '1' ? '0' : '1';
        const nuevoEstadoBool = nuevoEstado === '1';

        // Actualización optimista en el estado
        setForm(prev => ({ ...prev, pwa_activo: nuevoEstado }));

        try {
            const formData = new FormData();
            Object.keys({ ...form, pwa_activo: nuevoEstado }).forEach(k => {
                formData.append(k, (k === 'pwa_activo' ? nuevoEstado : (form[k] ?? '')));
            });

            const res = await fetch(`${baseURL}/configuracionPost.php`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            const data = await res.json();
            if (data && data.status === 'success') {
                notificarCambioConfiguracion(data.config);
                aplicarConfiguracionEnDOM(data.config);
                Swal.fire({
                    icon: nuevoEstadoBool ? 'success' : 'info',
                    title: nuevoEstadoBool ? '¡PWA Activada con Éxito!' : 'PWA Desactivada',
                    text: nuevoEstadoBool
                        ? 'La Aplicación Web Progresiva (PWA) ahora está activa. Tus clientes podrán instalar la app en Android, iOS y computadoras.'
                        : 'El aviso de instalación y soporte PWA ha sido desactivado.',
                    timer: 2300,
                    showConfirmButton: false
                });
            } else {
                throw new Error(data.error || 'No se pudo guardar el estado de la PWA');
            }
        } catch (error) {
            console.error('Error al actualizar estado PWA:', error);
            // Revertir
            setForm(prev => ({ ...prev, pwa_activo: form.pwa_activo }));
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo actualizar el estado de la PWA en el servidor.'
            });
        }
    };

    // Lanzar previsualización del modal de instalación en la pantalla actual
    const handleProbarPwaPrompt = () => {
        window.dispatchEvent(new CustomEvent('pwa_test_prompt'));
        Swal.fire({
            icon: 'info',
            title: 'Modal de Instalación PWA Desplegado',
            text: 'Se ha abierto la ventana emergente de instalación para que compruebes cómo la verán tus clientes en vivo.',
            timer: 2000,
            showConfirmButton: false
        });
    };

    const handleGuardar = async (e) => {
        if (e) e.preventDefault();

        if (!form.meta_title.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Campo Requerido',
                text: 'El título de la página (Meta Title) es indispensable para el posicionamiento en Google de la agencia.'
            });
            return;
        }

        setGuardando(true);

        const formData = new FormData();
        Object.keys(form).forEach(key => {
            formData.append(key, form[key] || '');
        });

        if (imagenArchivo) {
            formData.append('og_image_file', imagenArchivo);
        }
        if (logoArchivo) {
            formData.append('logo_file', logoArchivo);
        }
        if (faviconArchivo) {
            formData.append('favicon_file', faviconArchivo);
        }
        if (footerBgArchivo) {
            formData.append('footer_bg_image_file', footerBgArchivo);
        }

        try {
            const res = await fetch(`${baseURL}/configuracionPost.php`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            const data = await res.json();

            if (res.ok && data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: '¡Configuración Guardada!',
                    text: 'Los parámetros de Identidad, Datos de la Agencia, Colores, SEO y Redes Sociales se han guardado y aplicado con éxito.',
                    confirmButtonColor: '#059669'
                });

                if (data.config) {
                    setForm(prev => ({ ...prev, ...data.config }));
                    setImagenArchivo(null);
                    setImagenPreviewLocal('');
                    setLogoArchivo(null);
                    setLogoPreviewLocal('');
                    setFaviconArchivo(null);
                    setFaviconPreviewLocal('');
                    setFooterBgArchivo(null);
                    setFooterBgPreviewLocal('');
                    notificarCambioConfiguracion(data.config);
                    aplicarConfiguracionEnDOM(data.config);
                }
            } else {
                throw new Error(data.error || 'Error al guardar la configuración');
            }
        } catch (error) {
            console.error('Error al guardar:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'No se pudo conectar con el servidor para guardar los cambios.'
            });
        } finally {
            setGuardando(false);
        }
    };

    // Cálculos de longitud de caracteres
    const titleLen = form.meta_title?.length || 0;
    const descLen = form.meta_description?.length || 0;
    const ogTitleLen = form.og_title?.length || 0;
    const ogDescLen = form.og_description?.length || 0;

    const imagenMostrada = imagenPreviewLocal || form.og_image || '/logo.png';

    // Generar bloque HTML para Tab de Código
    const codigoGenerado = `<!-- ======================================================== -->
<!-- CONFIGURACIÓN SEO Y GOOGLE (AGENCIA DE VIAJES Y TURISMO) -->
<!-- ======================================================== -->
<title>${form.meta_title || 'SEYLIS TOURS - Agencia de Viajes y Turismo'}</title>
<meta name="description" content="${form.meta_description || ''}" />
<meta name="keywords" content="${form.meta_keywords || ''}" />
<meta name="author" content="${form.meta_author || form.site_name || 'SEYLIS TOURS'}" />
<meta name="robots" content="${form.robots_index || 'index, follow'}" />
<link rel="canonical" href="${form.canonical_url || 'https://tingoaventura.com/'}" />

${form.google_verification ? `<!-- Google Search Console -->
<meta name="google-site-verification" content="${form.google_verification}" />\n` : ''}${form.google_analytics_id ? `<!-- Google Analytics 4 (GA4) -->
<script async src="https://www.googletagmanager.com/gtag/js?id=${form.google_analytics_id}"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', '${form.google_analytics_id}');
</script>\n` : ''}<!-- ======================================================== -->
<!-- OPEN GRAPH & REDES SOCIALES (WHATSAPP, FACEBOOK)        -->
<!-- ======================================================== -->
<meta property="og:type" content="${form.og_type || 'website'}" />
<meta property="og:site_name" content="${form.site_name || 'SEYLIS TOURS'}" />
<meta property="og:locale" content="${form.og_locale || 'es_PE'}" />
<meta property="og:title" content="${form.og_title || form.meta_title || ''}" />
<meta property="og:description" content="${form.og_description || form.meta_description || ''}" />
<meta property="og:image" content="${form.og_image || ''}" />
<meta property="og:url" content="${form.canonical_url || 'https://tingoaventura.com/'}" />

<!-- ======================================================== -->
<!-- TWITTER / X CARDS                                       -->
<!-- ======================================================== -->
<meta name="twitter:card" content="${form.twitter_card || 'summary_large_image'}" />
<meta name="twitter:title" content="${form.og_title || form.meta_title || ''}" />
<meta name="twitter:description" content="${form.og_description || form.meta_description || ''}" />
<meta name="twitter:image" content="${form.og_image || ''}" />
${form.twitter_site ? `<meta name="twitter:site" content="${form.twitter_site}" />` : ''}`;

    const copiarCodigoPortapapeles = () => {
        navigator.clipboard.writeText(codigoGenerado);
        Swal.fire({
            icon: 'success',
            title: '¡Código Copiado!',
            text: 'Las etiquetas meta fueron copiadas al portapapeles.',
            timer: 1500,
            showConfirmButton: false
        });
    };

    return (
        <div className="seoConfigContainer">
            {/* Header del Módulo de la Agencia */}
            <div className="seoConfigHeader">
                <div className="seoConfigHeaderLeft">
                    <div className="seoIconBadge" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', color: '#059669' }}>
                        <FontAwesomeIcon icon={faPlane} />
                    </div>
                    <div>
                        <h2>Configuración General de la Agencia de Viajes</h2>
                        <p>
                            Gestiona la identidad visual, colores de marca, datos de contacto y licencias de turismo, optimización SEO para Google y tarjetas para compartir tours en redes sociales.
                        </p>
                    </div>
                </div>

                <div className="seoHeaderActions">
                    {/* Botón Destacado para Activar / Desactivar PWA */}
                    <button
                        type="button"
                        onClick={handleTogglePwaRapido}
                        className={`btnPwaQuickHeader ${String(form.pwa_activo) === '1' ? 'pwaActive' : 'pwaInactive'}`}
                        title={String(form.pwa_activo) === '1' ? "PWA Activada. Haz clic para desactivar" : "PWA Desactivada. Haz clic para activar"}
                    >
                        <FontAwesomeIcon icon={String(form.pwa_activo) === '1' ? faToggleOn : faToggleOff} />
                        <span>{String(form.pwa_activo) === '1' ? 'PWA: Activada' : 'Activar PWA'}</span>
                        <span className={`pwaDotStatus ${String(form.pwa_activo) === '1' ? 'active' : 'inactive'}`} />
                    </button>
                    <button
                        type="button"
                        onClick={cargarConfiguracion}
                        className="btnSecondarySeo"
                        title="Recargar configuración"
                        disabled={loading || guardando}
                    >
                        <FontAwesomeIcon icon={faSync} spin={loading} />
                        <span>Recargar</span>
                    </button>
                    <button
                        type="button"
                        onClick={handleGuardar}
                        className="btnPrimarySeo"
                        style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', boxShadow: '0 4px 14px rgba(5, 150, 105, 0.3)' }}
                        disabled={guardando}
                    >
                        <FontAwesomeIcon icon={faSave} />
                        <span>{guardando ? 'Guardando...' : 'Guardar Cambios'}</span>
                    </button>
                </div>
            </div>

            {/* Banner Informativo de la Agencia */}
            <div className="agencyHeroBanner">
                <div className="agencyHeroContent">
                    <div className="agencyHeroIcon">
                        <FontAwesomeIcon icon={faCompass} />
                    </div>
                    <div className="agencyHeroTexts">
                        <h4>{form.site_name || 'SEYLIS TOURS'} · Panel de Control de la Agencia</h4>
                        <p>{form.slogan || 'Especialistas en paquetes turísticos, excursiones guiadas, vuelos y hoteles.'}</p>
                    </div>
                </div>
                <div className="agencyHeroRightActions">
                    <button
                        type="button"
                        className={`heroPwaBadgeBtn ${String(form.pwa_activo) === '1' ? 'active' : 'inactive'}`}
                        onClick={handleTogglePwaRapido}
                        title="Haz clic para activar o desactivar la PWA"
                    >
                        <FontAwesomeIcon icon={faMobileAlt} />
                        <span>PWA: {String(form.pwa_activo) === '1' ? 'Activado' : 'Desactivado'}</span>
                        <span className="heroPwaBtnAction">{String(form.pwa_activo) === '1' ? 'Desactivar' : 'Activar'}</span>
                    </button>
                    <div className="agencyHeroBadge">
                        <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: 6 }} />
                        {form.registro_turismo || 'Agencia de Viajes Autorizada'}
                    </div>
                </div>
            </div>

            {/* Pestañas de Navegación del Módulo */}
            <div className="seoTabsBar">
                <button
                    type="button"
                    className={`seoTabBtn ${activeTab === 'colores' ? 'active' : ''}`}
                    onClick={() => setActiveTab('colores')}
                >
                    <FontAwesomeIcon icon={faPalette} />
                    <span>Identidad & Colores</span>
                </button>
                <button
                    type="button"
                    className={`seoTabBtn ${activeTab === 'datos_agencia' ? 'active' : ''}`}
                    onClick={() => setActiveTab('datos_agencia')}
                >
                    <FontAwesomeIcon icon={faSuitcaseRolling} />
                    <span>Datos de la Agencia & Atención</span>
                </button>
                <button
                    type="button"
                    className={`seoTabBtn ${activeTab === 'pwa' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pwa')}
                >
                    <FontAwesomeIcon icon={faMobileAlt} />
                    <span>PWA / App Móvil</span>
                    <span className={`pwaTabMiniPill ${String(form.pwa_activo) === '1' ? 'on' : 'off'}`}>
                        {String(form.pwa_activo) === '1' ? 'ACTIVO' : 'INACTIVO'}
                    </span>
                </button>
                <button
                    type="button"
                    className={`seoTabBtn ${activeTab === 'seo' ? 'active' : ''}`}
                    onClick={() => setActiveTab('seo')}
                >
                    <FontAwesomeIcon icon={faSearch} />
                    <span>Google & SEO Turístico</span>
                </button>
                <button
                    type="button"
                    className={`seoTabBtn ${activeTab === 'og' ? 'active' : ''}`}
                    onClick={() => setActiveTab('og')}
                >
                    <FontAwesomeIcon icon={faShareAlt} />
                    <span>Redes & Open Graph (Tours)</span>
                </button>
                <button
                    type="button"
                    className={`seoTabBtn ${activeTab === 'code' ? 'active' : ''}`}
                    onClick={() => setActiveTab('code')}
                >
                    <FontAwesomeIcon icon={faCode} />
                    <span>Código HTML & Consejos</span>
                </button>
            </div>

            {/* CONTENIDO DE PESTAÑAS */}
            <div className="seoTabContentWrapper">
                {/* ======================================================== */}
                {/* TAB 0: COLORES E IDENTIDAD (LOGOS, FAVICON Y COLORES)     */}
                {/* ======================================================== */}
                {activeTab === 'colores' && (
                    <div className="seoTabGrid">
                        {/* Columna Izquierda: Formulario de Identidad y Colores */}
                        <div className="seoFormCard">
                            <h3 className="sectionSubtitle">
                                <FontAwesomeIcon icon={faPalette} /> Identidad Visual y Paleta de Colores de la Agencia
                            </h3>

                            {/* Tarjeta de Control Rápido de PWA */}
                            <div className="identityCardBlock pwaHighlightCard">
                                <div className="identityCardHeader">
                                    <h4>
                                        <FontAwesomeIcon icon={faMobileAlt} style={{ color: '#059669' }} /> Aplicación Web Progresiva (PWA / App Móvil)
                                    </h4>
                                    <span className={`pwaStatusBadge ${String(form.pwa_activo) === '1' ? 'active' : 'inactive'}`}>
                                        {String(form.pwa_activo) === '1' ? '● PWA Activado' : '○ PWA Desactivado'}
                                    </span>
                                </div>
                                <p className="identityDescription">
                                    Permite a los viajeros instalar tu agencia de viajes como una aplicación móvil en celulares Android, iPhone y computadoras con un solo clic.
                                </p>
                                <div className="pwaCardActionsRow">
                                    <button
                                        type="button"
                                        onClick={handleTogglePwaRapido}
                                        className={`btnTogglePwaAction ${String(form.pwa_activo) === '1' ? 'btnDeactivate' : 'btnActivate'}`}
                                    >
                                        <FontAwesomeIcon icon={String(form.pwa_activo) === '1' ? faToggleOn : faToggleOff} />
                                        <span>{String(form.pwa_activo) === '1' ? 'Desactivar PWA' : 'Activar PWA Ahora'}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleProbarPwaPrompt}
                                        className="btnPreviewPwaAction"
                                    >
                                        <FontAwesomeIcon icon={faEye} /> Probar Modal en Pantalla
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('pwa')}
                                        className="btnConfigPwaAction"
                                    >
                                        Configuración PWA Avanzada →
                                    </button>
                                </div>
                            </div>

                            {/* 1. Logo del Sitio (Navbar Superior y Footer) */}
                            <div className="identityCardBlock">
                                <div className="identityCardHeader">
                                    <h4>
                                        <FontAwesomeIcon icon={faImage} style={{ color: '#059669' }} /> Logo Oficial de la Agencia
                                    </h4>
                                    <span className="identityBadgeTag">Navbar & Footer</span>
                                </div>
                                <p className="identityDescription">
                                    Carga el logotipo oficial de tu agencia de viajes. Se mostrará en alta definición en la barra superior (Navbar), en el menú móvil y en el pie de página (Footer).
                                </p>

                                <div className="identityUploadFlex">
                                    <div className="identityPreviewBox">
                                        <img
                                            src={logoPreviewLocal || form.logo || '/logo.png'}
                                            alt="Logo de la agencia"
                                            className="identityPreviewImg"
                                            onError={(e) => { e.target.src = '/logo.png'; }}
                                        />
                                    </div>
                                    <div className="identityUploadActions">
                                        <div className="identityUploadBtns">
                                            <input
                                                type="file"
                                                ref={logoInputRef}
                                                onChange={handleLogoChange}
                                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                                style={{ display: 'none' }}
                                            />
                                            <button
                                                type="button"
                                                className="btnUploadFile"
                                                onClick={() => logoInputRef.current && logoInputRef.current.click()}
                                            >
                                                <FontAwesomeIcon icon={faUpload} /> Seleccionar Logo
                                            </button>
                                            {(logoPreviewLocal || (form.logo && form.logo !== '/logo.png')) && (
                                                <button
                                                    type="button"
                                                    className="btnRemoveFile"
                                                    onClick={handleQuitarLogo}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} /> Restaurar Por Defecto
                                                </button>
                                            )}
                                        </div>
                                        <p className="identityFormatTip">
                                            Formatos recomendados: PNG transparente, SVG o WEBP. Tamaño ideal: horizontal o cuadrado nítido (ej: 300x120 px o 250x250 px).
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* 2. Favicon (Pestaña del Navegador) */}
                            <div className="identityCardBlock">
                                <div className="identityCardHeader">
                                    <h4>
                                        <FontAwesomeIcon icon={faGlobe} style={{ color: '#0284c7' }} /> Favicon (Ícono en el Navegador)
                                    </h4>
                                    <span className="identityBadgeTag">Pestaña Browser</span>
                                </div>
                                <p className="identityDescription">
                                    El favicon es el pequeño ícono distintivo que visualizan los viajeros en las pestañas de Chrome, Safari o Edge, y al guardar tu agencia en favoritos.
                                </p>

                                <div className="identityUploadFlex">
                                    <div className="identityPreviewBox faviconBox">
                                        <img
                                            src={faviconPreviewLocal || form.favicon || form.logo || '/logo.png'}
                                            alt="Favicon"
                                            className="identityPreviewImg"
                                            onError={(e) => { e.target.src = '/logo.png'; }}
                                        />
                                    </div>
                                    <div className="identityUploadActions">
                                        <div className="identityUploadBtns">
                                            <input
                                                type="file"
                                                ref={faviconInputRef}
                                                onChange={handleFaviconChange}
                                                accept=".ico,image/png,image/svg+xml,image/webp"
                                                style={{ display: 'none' }}
                                            />
                                            <button
                                                type="button"
                                                className="btnUploadFile"
                                                onClick={() => faviconInputRef.current && faviconInputRef.current.click()}
                                            >
                                                <FontAwesomeIcon icon={faUpload} /> Seleccionar Favicon
                                            </button>
                                            {(faviconPreviewLocal || (form.favicon && form.favicon !== '/logo.png')) && (
                                                <button
                                                    type="button"
                                                    className="btnRemoveFile"
                                                    onClick={handleQuitarFavicon}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} /> Restaurar
                                                </button>
                                            )}
                                        </div>
                                        <p className="identityFormatTip">
                                            Formatos recomendados: .ico, .png, .svg. Tamaño sugerido: 32x32 px o 64x64 px.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* 3. Color Primario: Barra Móvil, Modales y Botones de Reserva */}
                            <div className="identityCardBlock">
                                <div className="identityCardHeader">
                                    <h4>
                                        <FontAwesomeIcon icon={faMobileAlt} style={{ color: '#059669' }} /> Color Principal de Marca (Menú Móvil, Modales y Botones de Reserva)
                                    </h4>
                                    <span className="identityBadgeTag">Móvil & Botones</span>
                                </div>
                                <p className="identityDescription">
                                    Elige el color distintivo para la barra de navegación móvil, las cabeceras de las ventanas modales (Favoritos, Detalles de paquetes) y los botones de reserva y cotización.
                                </p>

                                <div className="colorPickerContainer">
                                    <div className="colorPickerControlRow">
                                        <div className="colorNativeWrapper" title="Haz clic para abrir el selector de color">
                                            <input
                                                type="color"
                                                name="color_menu_movil"
                                                value={form.color_menu_movil || '#059669'}
                                                onChange={handleInputChange}
                                                className="colorNativeInput"
                                            />
                                            <div
                                                className="colorNativeSwatch"
                                                style={{ backgroundColor: form.color_menu_movil || '#059669' }}
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            name="color_menu_movil"
                                            value={form.color_menu_movil || '#059669'}
                                            onChange={handleInputChange}
                                            placeholder="#059669"
                                            className="colorHexInput"
                                            maxLength={7}
                                        />
                                    </div>

                                    {/* Paleta sugerida para agencias de viajes y turismo */}
                                    <div className="colorPresetsGroup">
                                        <span className="colorPresetLabel">Colores turísticos recomendados:</span>
                                        {[
                                            { label: 'Verde Selva & Aventura', hex: '#059669' },
                                            { label: 'Azul Océano & Cielo', hex: '#0284C7' },
                                            { label: 'Turquesa Caribe', hex: '#0D9488' },
                                            { label: 'Azul Marino Royal', hex: '#1E40AF' },
                                            { label: 'Coral Atardecer Tropical', hex: '#EA580C' },
                                            { label: 'Dorado Sol & Arena', hex: '#D97706' },
                                            { label: 'Negro Carbón Elegante', hex: '#111827' }
                                        ].map(preset => (
                                            <button
                                                key={preset.hex}
                                                type="button"
                                                className={`colorPresetBtn ${form.color_menu_movil?.toLowerCase() === preset.hex.toLowerCase() ? 'active' : ''}`}
                                                style={{ backgroundColor: preset.hex }}
                                                title={preset.label}
                                                onClick={() => handleSelectColor('color_menu_movil', preset.hex)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* 4. Color del Footer (Pie de Página) */}
                            <div className="identityCardBlock">
                                <div className="identityCardHeader">
                                    <h4>
                                        <FontAwesomeIcon icon={faBrush} style={{ color: '#064E3B' }} /> Color del Footer (Pie de Página)
                                    </h4>
                                    <span className="identityBadgeTag">Footer Global</span>
                                </div>
                                <p className="identityDescription">
                                    Define el color de fondo para el pie de página de la web, donde se presentan los destinos, enlaces de interés, datos de contacto de la agencia y redes sociales.
                                </p>

                                <div className="colorPickerContainer">
                                    <div className="colorPickerControlRow">
                                        <div className="colorNativeWrapper" title="Haz clic para abrir el selector de color">
                                            <input
                                                type="color"
                                                name="color_footer"
                                                value={form.color_footer || '#064E3B'}
                                                onChange={handleInputChange}
                                                className="colorNativeInput"
                                            />
                                            <div
                                                className="colorNativeSwatch"
                                                style={{ backgroundColor: form.color_footer || '#064E3B' }}
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            name="color_footer"
                                            value={form.color_footer || '#064E3B'}
                                            onChange={handleInputChange}
                                            placeholder="#064E3B"
                                            className="colorHexInput"
                                            maxLength={7}
                                        />
                                    </div>

                                    {/* Paleta sugerida para Footer de viajes */}
                                    <div className="colorPresetsGroup">
                                        <span className="colorPresetLabel">Tonos elegantes para Footer:</span>
                                        {[
                                            { label: 'Verde Bosque Amazónico', hex: '#064E3B' },
                                            { label: 'Azul Océano Profundo', hex: '#0F172A' },
                                            { label: 'Azul Petróleo Oscuro', hex: '#1E293B' },
                                            { label: 'Teal Caribeño Oscuro', hex: '#134E4A' },
                                            { label: 'Azul Marino Intenso', hex: '#172554' },
                                            { label: 'Carbón Grafito', hex: '#18181B' }
                                        ].map(preset => (
                                            <button
                                                key={preset.hex}
                                                type="button"
                                                className={`colorPresetBtn ${form.color_footer?.toLowerCase() === preset.hex.toLowerCase() ? 'active' : ''}`}
                                                style={{ backgroundColor: preset.hex }}
                                                title={preset.label}
                                                onClick={() => handleSelectColor('color_footer', preset.hex)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* 5. Imagen Panorámica de Fondo del Footer */}
                            <div className="identityCardBlock">
                                <div className="identityCardHeader">
                                    <h4>
                                        <FontAwesomeIcon icon={faImage} style={{ color: '#0284c7' }} /> Imagen Panorámica de Fondo del Footer
                                    </h4>
                                    <span className="identityBadgeTag">Fondo Turístico</span>
                                </div>
                                <p className="identityDescription">
                                    Adjunta una foto panorámica de un destino turístico (playa, cataratas, montañas, selva). Se combinará elegantemente con el color del pie de página.
                                </p>

                                <div className="fileUploadGroup">
                                    <input
                                        type="file"
                                        ref={footerBgInputRef}
                                        onChange={handleFooterBgChange}
                                        accept="image/png, image/jpeg, image/webp, image/svg+xml"
                                        style={{ display: 'none' }}
                                    />

                                    {(footerBgPreviewLocal || form.footer_bg_image) ? (
                                        <div className="filePreviewBox footerBgPreviewBox">
                                            <div className="footerBgImageThumbWrapper">
                                                <img
                                                    src={footerBgPreviewLocal || form.footer_bg_image}
                                                    alt="Fondo Footer Turístico"
                                                    className="footerBgImgPreview"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            </div>
                                            <div className="filePreviewActions">
                                                <button
                                                    type="button"
                                                    onClick={() => footerBgInputRef.current?.click()}
                                                    className="btnUploadFile"
                                                >
                                                    <FontAwesomeIcon icon={faUpload} /> Cambiar Foto
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleQuitarFooterBg}
                                                    className="btnRemoveFile"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} /> Quitar Foto
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            className="fileDropZone"
                                            onClick={() => footerBgInputRef.current?.click()}
                                        >
                                            <FontAwesomeIcon icon={faImage} className="fileDropIcon" style={{ color: '#059669' }} />
                                            <span className="fileDropText">Haz clic para adjuntar foto panorámica de fondo para el Footer</span>
                                            <span className="fileDropHint">PNG, JPG, WEBP • Recomendado: formato panorámico (ej: 1920 × 500 px)</span>
                                        </div>
                                    )}
                                </div>

                                {/* Nivel de Transparencia de la Foto */}
                                <div className="transparencyControlBox">
                                    <div className="transparencyHeaderRow">
                                        <div className="transparencyTitleWrapper">
                                            <FontAwesomeIcon icon={faEye} style={{ color: '#059669' }} />
                                            <span className="transparencyLabel">Nivel de Transparencia de la Foto</span>
                                        </div>
                                        <div className="transparencyBadgesRow">
                                            <span className="badgeTransparency">
                                                {Math.round((1 - parseFloat(form.footer_bg_opacity !== undefined ? form.footer_bg_opacity : 0.70)) * 100)}% Transparencia
                                            </span>
                                            <span className="badgeOpacity">
                                                {Math.round(parseFloat(form.footer_bg_opacity !== undefined ? form.footer_bg_opacity : 0.70) * 100)}% Visible
                                            </span>
                                        </div>
                                    </div>

                                    <div className="transparencySliderRow">
                                        <span className="sliderBoundText">0% (Nítido)</span>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            step="5"
                                            value={Math.round((1 - parseFloat(form.footer_bg_opacity !== undefined ? form.footer_bg_opacity : 0.70)) * 100)}
                                            onChange={(e) => {
                                                const trans = parseInt(e.target.value, 10);
                                                const opacity = ((100 - trans) / 100).toFixed(2);
                                                setForm(prev => ({ ...prev, footer_bg_opacity: opacity }));
                                            }}
                                            className="transparencySlider"
                                        />
                                        <span className="sliderBoundText">100% (Transparente)</span>
                                    </div>

                                    <div className="transparencyPresetsRow">
                                        <span className="presetTitleText">Ajustes rápidos:</span>
                                        {[
                                            { label: '15% (Sutil)', trans: 85 },
                                            { label: '30% (Recomendado)', trans: 70 },
                                            { label: '50% (Equilibrado)', trans: 50 },
                                            { label: '70% (Visible)', trans: 30 }
                                        ].map(preset => {
                                            const currentTrans = Math.round((1 - parseFloat(form.footer_bg_opacity !== undefined ? form.footer_bg_opacity : 0.70)) * 100);
                                            const isActive = currentTrans === preset.trans;
                                            return (
                                                <button
                                                    key={preset.trans}
                                                    type="button"
                                                    onClick={() => {
                                                        const opacity = ((100 - preset.trans) / 100).toFixed(2);
                                                        setForm(prev => ({ ...prev, footer_bg_opacity: opacity }));
                                                    }}
                                                    className={`btnTransparencyPreset ${isActive ? 'active' : ''}`}
                                                >
                                                    {preset.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Columna Derecha: Simuladores en Tiempo Real de la Agencia */}
                        <div className="seoPreviewCard">
                            <h3 className="sectionSubtitle">
                                <FontAwesomeIcon icon={faEye} /> Simuladores en Tiempo Real
                            </h3>
                            <p className="previewBoxIntro">
                                Observa cómo se visualiza tu agencia de viajes con el logo, favicon y colores seleccionados:
                            </p>

                            {/* Mockup 1: Pestaña del Navegador */}
                            <div className="mockupSectionBlock">
                                <h5 className="mockupTitle">
                                    <FontAwesomeIcon icon={faGlobe} style={{ color: '#0284c7' }} /> 1. Pestaña del Navegador (Favicon y Título)
                                </h5>
                                <div className="browserTabMockup">
                                    <div className="browserTabItem">
                                        <img
                                            src={faviconPreviewLocal || form.favicon || logoPreviewLocal || form.logo || '/logo.png'}
                                            alt="Favicon"
                                            className="browserTabFavicon"
                                            onError={(e) => { e.target.src = '/logo.png'; }}
                                        />
                                        <span className="browserTabLabel">
                                            {form.meta_title || `${form.site_name || 'SEYLIS TOURS'} - Agencia de Viajes`}
                                        </span>
                                        <span className="browserTabClose">✕</span>
                                    </div>
                                </div>
                                <div className="browserUrlBarMockup">
                                    <FontAwesomeIcon icon={faLock} className="browserUrlLock" />
                                    <span className="browserUrlText">
                                        {form.canonical_url || 'https://tingoaventura.com'}
                                    </span>
                                </div>
                            </div>

                            {/* Mockup 2: Barra Móvil en Smartphone de Viajes */}
                            <div className="mockupSectionBlock">
                                <h5 className="mockupTitle">
                                    <FontAwesomeIcon icon={faMobileAlt} style={{ color: '#059669' }} /> 2. Barra de Menú Móvil en Celulares
                                </h5>
                                <div className="mobileNavPhoneFrame">
                                    <div className="phoneTopNotch">
                                        <div className="phoneNotchPill"></div>
                                    </div>
                                    <div
                                        className="mobileNavPreviewBar"
                                        style={{ backgroundColor: form.color_menu_movil || '#059669' }}
                                    >
                                        <img
                                            src={logoPreviewLocal || form.logo || '/logo.png'}
                                            alt="Logo Móvil"
                                            className="mobileNavLogoImg"
                                            onError={(e) => { e.target.src = '/logo.png'; }}
                                        />
                                        <div className="mobileNavActionIcons">
                                            <FontAwesomeIcon icon={faHeart} title="Favoritos" />
                                            <FontAwesomeIcon icon={faSearch} title="Buscar Tours" />
                                            <div className="mobileNavHamburger" title="Menú">
                                                <span></span>
                                                <span></span>
                                                <span></span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mobilePhoneContentDummy">
                                        <span className="dummyBannerText">
                                            {form.site_name || 'SEYLIS TOURS'}
                                        </span>
                                        <span className="dummyBannerSub">
                                            {form.slogan || 'Especialistas en Paquetes Turísticos, Vuelos y Tours'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Mockup 3: Footer de la Agencia */}
                            <div className="mockupSectionBlock">
                                <h5 className="mockupTitle">
                                    <FontAwesomeIcon icon={faBrush} style={{ color: '#064E3B' }} /> 3. Pie de Página (Footer de la Agencia)
                                </h5>
                                <div
                                    className="footerMockupCard"
                                    style={{
                                        backgroundColor: form.color_footer || '#064E3B',
                                        '--footer-mockup-bg': (footerBgPreviewLocal || form.footer_bg_image)
                                            ? `url("${footerBgPreviewLocal || form.footer_bg_image}")`
                                            : 'none',
                                        '--footer-mockup-opacity': form.footer_bg_opacity !== undefined
                                            ? form.footer_bg_opacity
                                            : '0.70'
                                    }}
                                >
                                    <div className="footerMockupTop">
                                        <img
                                            src={logoPreviewLocal || form.logo || '/logo.png'}
                                            alt="Logo Footer"
                                            className="footerMockupLogoImg"
                                            onError={(e) => { e.target.src = '/logo.png'; }}
                                        />
                                        <div>
                                            <h4 className="footerMockupBrandName">
                                                {form.site_name || 'SEYLIS TOURS'}
                                            </h4>
                                            <span style={{ fontSize: '0.75rem', opacity: 0.85, display: 'block' }}>
                                                {form.slogan || 'Tu agencia de confianza'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="footerMockupLinksRow">
                                        <span>Tours & Paquetes</span>
                                        <span>Quiénes Somos</span>
                                        <span>Destinos</span>
                                        <span>Contacto & Reservas</span>
                                    </div>
                                    <div className="footerMockupSocials">
                                        <div className="footerMockupSocialIcon"><i className="fa fa-facebook"></i></div>
                                        <div className="footerMockupSocialIcon"><i className="fa fa-instagram"></i></div>
                                        <div className="footerMockupSocialIcon"><i className="fa fa-whatsapp"></i></div>
                                        <div className="footerMockupSocialIcon"><i className="fa fa-tiktok"></i></div>
                                    </div>
                                </div>
                            </div>

                            {/* Mockup 4: Ventana Modal y Botón de Cotización de Tour */}
                            <div className="mockupSectionBlock">
                                <h5 className="mockupTitle">
                                    <FontAwesomeIcon icon={faTicketAlt} style={{ color: '#059669' }} /> 4. Reserva / Cotización de Tour y Botones
                                </h5>
                                <div className="modalMockupCard">
                                    <div
                                        className="modalMockupHeader"
                                        style={{ backgroundColor: form.color_menu_movil || '#059669' }}
                                    >
                                        <div className="modalMockupHeaderLeft">
                                            <span>←</span>
                                            <span>Reservas / Tours Favoritos</span>
                                        </div>
                                        <span className="modalMockupBadge">Cerrar ✕</span>
                                    </div>
                                    <div className="modalMockupBody">
                                        <div className="modalMockupItem">
                                            <div className="modalMockupItemImg" style={{ background: '#e0f2fe' }}>
                                                <FontAwesomeIcon icon={faPlane} style={{ color: '#0284c7' }} />
                                            </div>
                                            <div className="modalMockupItemInfo">
                                                <span className="modalMockupItemTitle">Tour Cueva de las Lechuzas Full Day</span>
                                                <span className="modalMockupItemPrice">
                                                    {form.moneda_default === 'USD' ? '$ 45.00' : 'S/ 120.00'} por pasajero
                                                </span>
                                            </div>
                                            <div
                                                className="modalMockupItemQty"
                                                style={{ backgroundColor: form.color_menu_movil || '#059669' }}
                                            >
                                                ✓
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="modalMockupActionBtn"
                                            style={{ backgroundColor: form.color_menu_movil || '#059669' }}
                                        >
                                            <FontAwesomeIcon icon={faPhoneAlt} style={{ marginRight: 6 }} />
                                            Cotizar Tour por WhatsApp
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ======================================================== */}
                {/* TAB 1: DATOS DE LA AGENCIA & ATENCIÓN AL VIAJERO        */}
                {/* ======================================================== */}
                {activeTab === 'datos_agencia' && (
                    <div className="seoTabGrid">
                        {/* Columna Izquierda: Formulario de Datos de la Agencia */}
                        <div className="seoFormCard">
                            <h3 className="sectionSubtitle">
                                <FontAwesomeIcon icon={faSuitcaseRolling} /> Datos Operativos y Atención al Viajero
                            </h3>

                            {/* Nombre Oficial y Slogan */}
                            <div className="seoFormGroup">
                                <label htmlFor="site_name">Nombre Comercial de la Agencia de Viajes *</label>
                                <input
                                    id="site_name"
                                    type="text"
                                    name="site_name"
                                    value={form.site_name}
                                    onChange={handleInputChange}
                                    placeholder="Ej: SEYLIS TOURS"
                                    className="seoInput"
                                />
                                <span className="seoHelperText">
                                    Nombre que aparecerá en la cabecera, pie de página, presupuestos y metadatos oficiales.
                                </span>
                            </div>

                            {/* Slogan */}
                            <div className="seoFormGroup">
                                <label htmlFor="slogan">Lema o Slogan de la Agencia</label>
                                <input
                                    id="slogan"
                                    type="text"
                                    name="slogan"
                                    value={form.slogan}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Tu agencia de confianza, viajes y experiencias inolvidables"
                                    className="seoInput"
                                />
                                <div className="sloganSuggestionsRow">
                                    <span className="quickKeywordLabel">Sugerencias:</span>
                                    {[
                                        'Tu agencia de confianza, viajes y experiencias inolvidables',
                                        'Explora el Perú y el mundo con expertos en turismo',
                                        'Los mejores paquetes turísticos y tours al mejor precio',
                                        'Aventura, naturaleza y descanso garantizado'
                                    ].map(sug => (
                                        <button
                                            key={sug}
                                            type="button"
                                            className="btnSloganSuggestion"
                                            onClick={() => setForm(prev => ({ ...prev, slogan: sug }))}
                                        >
                                            + {sug}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* WhatsApp y Teléfono de Reservas */}
                            <div className="seoFormRowTwo">
                                <div className="seoFormGroup">
                                    <label htmlFor="whatsapp_reservas">
                                        <i className="fa fa-whatsapp" style={{ color: '#25D366', marginRight: 6 }}></i>
                                        WhatsApp Oficial de Reservas (Solo números)
                                    </label>
                                    <input
                                        id="whatsapp_reservas"
                                        type="text"
                                        name="whatsapp_reservas"
                                        value={form.whatsapp_reservas}
                                        onChange={handleInputChange}
                                        placeholder="Ej: 51987654321 (con código de país sin +)"
                                        className="seoInput codeFont"
                                    />
                                    <span className="seoHelperText">Para enviar presupuestos y cotizaciones directas por WhatsApp.</span>
                                </div>

                                <div className="seoFormGroup">
                                    <label htmlFor="telefono_reservas">
                                        <FontAwesomeIcon icon={faPhoneAlt} style={{ color: '#059669', marginRight: 6 }} />
                                        Teléfono de Contacto Visible
                                    </label>
                                    <input
                                        id="telefono_reservas"
                                        type="text"
                                        name="telefono_reservas"
                                        value={form.telefono_reservas}
                                        onChange={handleInputChange}
                                        placeholder="Ej: +51 987 654 321 o (062) 562145"
                                        className="seoInput"
                                    />
                                    <span className="seoHelperText">Número telefónico visible para los viajeros.</span>
                                </div>
                            </div>

                            {/* Correo y Dirección */}
                            <div className="seoFormRowTwo">
                                <div className="seoFormGroup">
                                    <label htmlFor="email_reservas">
                                        <FontAwesomeIcon icon={faEnvelope} style={{ color: '#0284c7', marginRight: 6 }} />
                                        Email de Reservas y Cotizaciones
                                    </label>
                                    <input
                                        id="email_reservas"
                                        type="email"
                                        name="email_reservas"
                                        value={form.email_reservas}
                                        onChange={handleInputChange}
                                        placeholder="reservas@tingoaventura.com"
                                        className="seoInput"
                                    />
                                </div>

                                <div className="seoFormGroup">
                                    <label htmlFor="registro_turismo">
                                        <FontAwesomeIcon icon={faShieldAlt} style={{ color: '#d97706', marginRight: 6 }} />
                                        Licencia o Registro de Turismo
                                    </label>
                                    <input
                                        id="registro_turismo"
                                        type="text"
                                        name="registro_turismo"
                                        value={form.registro_turismo}
                                        onChange={handleInputChange}
                                        placeholder="Ej: DIRCETUR / MINCETUR Licencia N° 2026-AV"
                                        className="seoInput"
                                    />
                                </div>
                            </div>

                            {/* Dirección Física y Horarios */}
                            <div className="seoFormGroup">
                                <label htmlFor="direccion_agencia">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#ef4444', marginRight: 6 }} />
                                    Dirección Física de la Agencia / Oficina
                                </label>
                                <input
                                    id="direccion_agencia"
                                    type="text"
                                    name="direccion_agencia"
                                    value={form.direccion_agencia}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Av. Alameda Perú 450 - Tingo María, Huánuco, Perú"
                                    className="seoInput"
                                />
                            </div>

                            <div className="seoFormRowTwo">
                                <div className="seoFormGroup">
                                    <label htmlFor="horario_atencion">
                                        <FontAwesomeIcon icon={faClock} style={{ color: '#8b5cf6', marginRight: 6 }} />
                                        Horario de Atención
                                    </label>
                                    <input
                                        id="horario_atencion"
                                        type="text"
                                        name="horario_atencion"
                                        value={form.horario_atencion}
                                        onChange={handleInputChange}
                                        placeholder="Ej: Lun - Sáb: 8:00 AM - 7:30 PM"
                                        className="seoInput"
                                    />
                                </div>

                                <div className="seoFormGroup">
                                    <label>
                                        <FontAwesomeIcon icon={faCoins} style={{ color: '#f59e0b', marginRight: 6 }} />
                                        Moneda Predeterminada de Paquetes
                                    </label>
                                    <div className="currencyBtnGroup">
                                        <button
                                            type="button"
                                            className={`currencyBtn ${form.moneda_default === 'PEN' ? 'active' : ''}`}
                                            onClick={() => setForm(prev => ({ ...prev, moneda_default: 'PEN' }))}
                                        >
                                            <span>Soles (PEN S/)</span>
                                        </button>
                                        <button
                                            type="button"
                                            className={`currencyBtn ${form.moneda_default === 'USD' ? 'active' : ''}`}
                                            onClick={() => setForm(prev => ({ ...prev, moneda_default: 'USD' }))}
                                        >
                                            <span>Dólares (USD $)</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Asistencia de Emergencia 24/7 */}
                            <div className="seoFormGroup">
                                <label htmlFor="mensaje_emergencia">
                                    <FontAwesomeIcon icon={faShieldAlt} style={{ color: '#059669', marginRight: 6 }} />
                                    Mensaje de Soporte o Asistencia 24/7
                                </label>
                                <input
                                    id="mensaje_emergencia"
                                    type="text"
                                    name="mensaje_emergencia"
                                    value={form.mensaje_emergencia}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Asistencia personalizada y soporte al viajero 24/7"
                                    className="seoInput"
                                />
                                <span className="seoHelperText">Brinda confianza y seguridad a los pasajeros durante sus viajes.</span>
                            </div>
                        </div>

                        {/* Columna Derecha: Carnet / Ficha Oficial de la Agencia */}
                        <div className="seoPreviewCard">
                            <h3 className="sectionSubtitle">
                                <FontAwesomeIcon icon={faShieldAlt} /> Ficha Oficial de la Agencia de Viajes
                            </h3>
                            <p className="previewBoxIntro">
                                Esta credencial resume los datos de contacto y licencias que respaldan la seguridad y formalidad de tus servicios turísticos:
                            </p>

                            <div className="agencyCertCard">
                                <div className="agencyCertHeader">
                                    <div className="agencyCertLogoWrap">
                                        <img
                                            src={logoPreviewLocal || form.logo || '/logo.png'}
                                            alt="Logo"
                                            className="agencyCertLogoImg"
                                            onError={(e) => { e.target.src = '/logo.png'; }}
                                        />
                                        <div className="agencyCertTitles">
                                            <h5>{form.site_name || 'SEYLIS TOURS'}</h5>
                                            <span>{form.slogan || 'Agencia de Viajes y Turismo'}</span>
                                        </div>
                                    </div>
                                    <div className="agencyCertStamp">
                                        Agencia Verificada
                                    </div>
                                </div>

                                <div className="agencyCertBody">
                                    <div className="agencyCertRow">
                                        <FontAwesomeIcon icon={faShieldAlt} />
                                        <div>
                                            <strong>Licencia / Registro:</strong>
                                            <span>{form.registro_turismo || 'MINCETUR / DIRCETUR Autorizado'}</span>
                                        </div>
                                    </div>

                                    <div className="agencyCertRow">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                                        <div>
                                            <strong>Oficina:</strong>
                                            <span>{form.direccion_agencia || 'Av. Alameda Perú 450 - Tingo María'}</span>
                                        </div>
                                    </div>

                                    <div className="agencyCertRow">
                                        <FontAwesomeIcon icon={faPhoneAlt} />
                                        <div>
                                            <strong>Reservas y Cotizaciones:</strong>
                                            <span>{form.telefono_reservas || '+51 987 654 321'}</span>
                                        </div>
                                    </div>

                                    <div className="agencyCertRow">
                                        <FontAwesomeIcon icon={faEnvelope} />
                                        <div>
                                            <strong>Correo Oficial:</strong>
                                            <span>{form.email_reservas || 'reservas@tingoaventura.com'}</span>
                                        </div>
                                    </div>

                                    <div className="agencyCertRow">
                                        <FontAwesomeIcon icon={faClock} />
                                        <div>
                                            <strong>Horario:</strong>
                                            <span>{form.horario_atencion || 'Lun - Sáb: 8:00 AM - 7:30 PM'}</span>
                                        </div>
                                    </div>

                                    <div className="agencyCertRow">
                                        <FontAwesomeIcon icon={faCoins} />
                                        <div>
                                            <strong>Moneda de Cotización:</strong>
                                            <span>{form.moneda_default === 'USD' ? 'Dólares Americanos ($ USD)' : 'Soles Peruanos (S/ PEN)'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="agencyTrustBadges">
                                    <div className="trustBadgeItem">
                                        <FontAwesomeIcon icon={faShieldAlt} />
                                        <span>Viajes Seguros</span>
                                    </div>
                                    <div className="trustBadgeItem">
                                        <FontAwesomeIcon icon={faStar} />
                                        <span>Guías Oficiales</span>
                                    </div>
                                    <div className="trustBadgeItem">
                                        <FontAwesomeIcon icon={faPhoneAlt} />
                                        <span>Soporte 24/7</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ======================================================== */}
                {/* TAB 2: MOTORES DE BÚSQUEDA Y GOOGLE (SEO)               */}
                {/* ======================================================== */}
                {activeTab === 'seo' && (
                    <div className="seoTabGrid">
                        {/* Columna Izquierda: Formulario SEO para Agencia de Viajes */}
                        <div className="seoFormCard">
                            <h3 className="sectionSubtitle">
                                <FontAwesomeIcon icon={faGlobe} /> Parámetros de Búsqueda para Google y Turismo
                            </h3>

                            {/* Meta Title */}
                            <div className="seoFormGroup">
                                <div className="labelFlex">
                                    <label htmlFor="meta_title">Título para Buscadores (Meta Title) *</label>
                                    <span className={`charCount ${titleLen > 65 ? 'warning' : 'good'}`}>
                                        {titleLen} / 60 caracteres recomendados
                                    </span>
                                </div>
                                <input
                                    id="meta_title"
                                    type="text"
                                    name="meta_title"
                                    value={form.meta_title}
                                    onChange={handleInputChange}
                                    placeholder="Ej: SEYLIS TOURS - Agencia de Viajes, Paquetes Turísticos y Tours"
                                    className="seoInput"
                                />
                                <span className="seoHelperText">
                                    Título principal en color azul que verán los turistas al buscar en Google.
                                </span>
                            </div>

                            {/* Meta Description */}
                            <div className="seoFormGroup">
                                <div className="labelFlex">
                                    <label htmlFor="meta_description">Descripción para Buscadores (Meta Description)</label>
                                    <span className={`charCount ${descLen > 165 ? 'warning' : 'good'}`}>
                                        {descLen} / 155 caracteres recomendados
                                    </span>
                                </div>
                                <textarea
                                    id="meta_description"
                                    name="meta_description"
                                    rows="3"
                                    value={form.meta_description}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Descubre los mejores destinos con SEYLIS TOURS. Paquetes turísticos, tours guiados, vuelos, hoteles y excursiones inolvidables en Perú y el mundo."
                                    className="seoTextarea"
                                />
                                <span className="seoHelperText">
                                    Texto persuasivo de 1 a 2 líneas que convence al viajero de hacer clic sobre tu enlace.
                                </span>
                            </div>

                            {/* Palabras Clave de Viajes */}
                            <div className="seoFormGroup">
                                <label htmlFor="meta_keywords">Palabras Clave Turísticas (Keywords separadas por coma)</label>
                                <input
                                    id="meta_keywords"
                                    type="text"
                                    name="meta_keywords"
                                    value={form.meta_keywords}
                                    onChange={handleInputChange}
                                    placeholder="agencia de viajes, paquetes turisticos, tours tingo maria, vuelos baratos"
                                    className="seoInput"
                                />
                                <div className="quickKeywordsRow">
                                    <span className="quickKeywordLabel">Sugerencias turísticas:</span>
                                    {[
                                        'Paquetes Turísticos',
                                        'Tours y Excursiones',
                                        'Tingo María Aventura',
                                        'Turismo Selva Central',
                                        'Vuelos y Boletos',
                                        'Hoteles Todo Incluido',
                                        'Vacaciones Familiares',
                                        'Ecoturismo y Naturaleza',
                                        'Tours Full Day',
                                        'Asistencia de Viaje'
                                    ].map(tag => (
                                        <button
                                            key={tag}
                                            type="button"
                                            className="tagPillBtn"
                                            onClick={() => agregarKeyword(tag)}
                                        >
                                            + {tag}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Fila de URL Canónica y Autor */}
                            <div className="seoFormRowTwo">
                                <div className="seoFormGroup">
                                    <label htmlFor="canonical_url">URL Canónica Principal de la Web</label>
                                    <input
                                        id="canonical_url"
                                        type="url"
                                        name="canonical_url"
                                        value={form.canonical_url}
                                        onChange={handleInputChange}
                                        placeholder="https://tingoaventura.com/"
                                        className="seoInput"
                                    />
                                    <span className="seoHelperText">Dominio oficial con https://</span>
                                </div>

                                <div className="seoFormGroup">
                                    <label htmlFor="meta_author">Autor / Empresa Titular</label>
                                    <input
                                        id="meta_author"
                                        type="text"
                                        name="meta_author"
                                        value={form.meta_author}
                                        onChange={handleInputChange}
                                        placeholder="SEYLIS TOURS"
                                        className="seoInput"
                                    />
                                </div>
                            </div>

                            {/* Posicionamiento en Google */}
                            <h3 className="sectionSubtitle mt-4">
                                <FontAwesomeIcon icon={faChartLine} /> Integración con Herramientas de Google
                            </h3>

                            {/* Google Search Console */}
                            <div className="seoFormGroup">
                                <label htmlFor="google_verification">
                                    <i className="fa fa-google" style={{ color: '#ea4335', marginRight: 6 }}></i>
                                    Código de Verificación de Google Search Console
                                </label>
                                <input
                                    id="google_verification"
                                    type="text"
                                    name="google_verification"
                                    value={form.google_verification}
                                    onChange={handleInputChange}
                                    placeholder="Ej: ABcDeF1234567890XYZ o google-site-verification=..."
                                    className="seoInput codeFont"
                                />
                                <span className="seoHelperText">
                                    Permite a Google Search Console indexar tus tours y paquetes en Google en cuestión de horas.
                                </span>
                            </div>

                            {/* Google Analytics ID & Robots */}
                            <div className="seoFormRowTwo">
                                <div className="seoFormGroup">
                                    <label htmlFor="google_analytics_id">ID de Google Analytics 4 (GA4)</label>
                                    <input
                                        id="google_analytics_id"
                                        type="text"
                                        name="google_analytics_id"
                                        value={form.google_analytics_id}
                                        onChange={handleInputChange}
                                        placeholder="Ej: G-XXXXXXXXXX"
                                        className="seoInput codeFont"
                                    />
                                    <span className="seoHelperText">Mide visitantes, turistas interesados y reservas.</span>
                                </div>

                                <div className="seoFormGroup">
                                    <label htmlFor="robots_index">
                                        <FontAwesomeIcon icon={faRobot} style={{ marginRight: 6 }} />
                                        Indexación (Robots Meta)
                                    </label>
                                    <select
                                        id="robots_index"
                                        name="robots_index"
                                        value={form.robots_index}
                                        onChange={handleInputChange}
                                        className="seoSelect"
                                    >
                                        <option value="index, follow">index, follow (Recomendado: Indexar y posicionar)</option>
                                        <option value="noindex, follow">noindex, follow (Ocultar de Google)</option>
                                        <option value="index, nofollow">index, nofollow (Indexar sin seguir enlaces)</option>
                                        <option value="noindex, nofollow">noindex, nofollow (Bloquear completamente)</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Columna Derecha: Vista Previa en Vivo de Google SERP */}
                        <div className="seoPreviewCard">
                            <div className="previewCardHeader">
                                <div className="previewCardBadge googleBadge">
                                    <i className="fa fa-google"></i> Vista Previa en Google
                                </div>
                                <span className="previewLiveIndicator">● En vivo</span>
                            </div>

                            <p className="previewCardTip">
                                Así se mostrará tu agencia de viajes cuando los viajeros busquen paquetes o tours en Google:
                            </p>

                            {/* Google SERP Snippet Box */}
                            <div className="googleSerpBox">
                                <div className="googleSerpHeader">
                                    <div className="googleFaviconCircle">
                                        <img
                                            src={faviconPreviewLocal || form.favicon || form.logo || '/logo.png'}
                                            alt="Logo"
                                            onError={(e) => { e.target.src = '/logo.png'; }}
                                        />
                                    </div>
                                    <div className="googleUrlBreadcrumb">
                                        <span className="googleSiteName">{form.site_name || 'SEYLIS TOURS'}</span>
                                        <span className="googleUrl">
                                            {form.canonical_url ? form.canonical_url.replace(/^https?:\/\//, '').replace(/\/$/, '') : 'tingoaventura.com'}
                                        </span>
                                    </div>
                                </div>

                                <div className="googleSerpTitle">
                                    {form.meta_title || `${form.site_name || 'SEYLIS TOURS'} - Agencia de Viajes y Paquetes Turísticos`}
                                </div>

                                <div className="googleSerpSnippet">
                                    {form.meta_description || 'Descubre los mejores destinos con nuestra agencia de viajes. Paquetes turísticos, tours guiados, vuelos baratos, hoteles todo incluido y asesoría personalizada.'}
                                </div>

                                {/* Rating simulado enriquecido para viajes */}
                                <div className="googleRichRating">
                                    <span className="stars">★★★★★</span>
                                    <span className="ratingText">Calificación: 4.9 · 185 reseñas de viajeros · Agencia Certificada</span>
                                </div>
                            </div>

                            {/* Checklist SEO */}
                            <div className="seoChecklist">
                                <h4>
                                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#059669', marginRight: 6 }} />
                                    Auditoría Rápida de Posicionamiento Turístico
                                </h4>
                                <ul className="checklistUl">
                                    <li className={titleLen >= 30 && titleLen <= 65 ? 'checkOk' : 'checkWarn'}>
                                        <span>Longitud de título ({titleLen} caracteres)</span>
                                        <strong>{titleLen >= 30 && titleLen <= 65 ? 'Óptimo' : 'Ajustar'}</strong>
                                    </li>
                                    <li className={descLen >= 70 && descLen <= 165 ? 'checkOk' : 'checkWarn'}>
                                        <span>Longitud de descripción ({descLen} caracteres)</span>
                                        <strong>{descLen >= 70 && descLen <= 165 ? 'Óptimo' : 'Ajustar'}</strong>
                                    </li>
                                    <li className={form.canonical_url ? 'checkOk' : 'checkWarn'}>
                                        <span>URL Canónica configurada</span>
                                        <strong>{form.canonical_url ? 'Correcto' : 'Vacío'}</strong>
                                    </li>
                                    <li className={form.google_verification ? 'checkOk' : 'checkPending'}>
                                        <span>Google Search Console</span>
                                        <strong>{form.google_verification ? 'Vinculado' : 'Pendiente'}</strong>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {/* ======================================================== */}
                {/* TAB 3: REDES SOCIALES Y OPEN GRAPH (OG IMAGE)            */}
                {/* ======================================================== */}
                {activeTab === 'og' && (
                    <div className="seoTabGrid">
                        {/* Columna Izquierda: Configuración OG & Imagen de Tours */}
                        <div className="seoFormCard">
                            <div className="sectionHeaderWithAction">
                                <h3 className="sectionSubtitle" style={{ margin: 0 }}>
                                    <FontAwesomeIcon icon={faShareAlt} /> Tarjetas para WhatsApp, Facebook y Redes Sociales
                                </h3>
                                <button
                                    type="button"
                                    onClick={handleCopiarDeSEO}
                                    className="btnPillOutline"
                                    title="Copiar título y descripción de la pestaña SEO"
                                >
                                    <FontAwesomeIcon icon={faCopy} /> Copiar desde SEO
                                </button>
                            </div>

                            {/* Título Open Graph */}
                            <div className="seoFormGroup mt-3">
                                <div className="labelFlex">
                                    <label htmlFor="og_title">Título para Redes Sociales (og:title)</label>
                                    <span className="charCount">{ogTitleLen} caracteres</span>
                                </div>
                                <input
                                    id="og_title"
                                    type="text"
                                    name="og_title"
                                    value={form.og_title}
                                    onChange={handleInputChange}
                                    placeholder={form.meta_title || "SEYLIS TOURS | Agencia de Viajes y Paquetes Turísticos"}
                                    className="seoInput"
                                />
                            </div>

                            {/* Descripción Open Graph */}
                            <div className="seoFormGroup">
                                <div className="labelFlex">
                                    <label htmlFor="og_description">Descripción para Redes Sociales (og:description)</label>
                                    <span className="charCount">{ogDescLen} caracteres</span>
                                </div>
                                <textarea
                                    id="og_description"
                                    name="og_description"
                                    rows="3"
                                    value={form.og_description}
                                    onChange={handleInputChange}
                                    placeholder={form.meta_description || "¡Planifica tus próximas vacaciones con SEYLIS TOURS! Paquetes turísticos completos, tours guiados y vuelos."}
                                    className="seoTextarea"
                                />
                            </div>

                            {/* Subida de Imagen OG (Portada de Viaje) */}
                            <div className="seoFormGroup">
                                <label>
                                    <FontAwesomeIcon icon={faImage} style={{ marginRight: 6, color: '#059669' }} />
                                    Foto de Portada para Redes Sociales (OG Image: 1200 x 630 px) *
                                </label>

                                <div className="ogUploadArea">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept="image/png, image/jpeg, image/webp"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                        id="ogImageFileInput"
                                    />

                                    <div className="uploadTriggerBox" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                                        <FontAwesomeIcon icon={faUpload} className="uploadIconLarge" style={{ color: '#059669' }} />
                                        <div className="uploadTexts">
                                            <strong>Haz clic aquí para seleccionar una foto de portada turística</strong>
                                            <span>Formatos: JPG, PNG, WEBP · Resolución recomendada: 1200 x 630 px (panorámica)</span>
                                        </div>
                                    </div>

                                    {/* Opción de URL directa */}
                                    <div className="ogUrlInputRow">
                                        <label htmlFor="og_image" style={{ fontSize: '0.85rem' }}>O pega un enlace de imagen externo:</label>
                                        <input
                                            id="og_image"
                                            type="url"
                                            name="og_image"
                                            value={form.og_image}
                                            onChange={handleInputChange}
                                            placeholder="https://tudominio.com/imagenes/portada-viajes.jpg"
                                            className="seoInput"
                                        />
                                    </div>

                                    {/* Previsualización de la imagen actual */}
                                    {imagenMostrada && (
                                        <div className="currentOgImageBanner">
                                            <img src={imagenMostrada} alt="Vista previa OG de viajes" />
                                            <div className="imageOverlayActions">
                                                <span className="badgeDim">1200 x 630 px</span>
                                                <button
                                                    type="button"
                                                    onClick={handleQuitarImagen}
                                                    className="btnRemoveImg"
                                                    title="Quitar foto"
                                                >
                                                    Quitar Foto
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Parámetros de Twitter / X */}
                            <h3 className="sectionSubtitle mt-4">
                                <i className="fa fa-twitter"></i> Parámetros de Twitter / X
                            </h3>

                            <div className="seoFormRowTwo">
                                <div className="seoFormGroup">
                                    <label htmlFor="twitter_card">Tipo de Tarjeta (Twitter Card)</label>
                                    <select
                                        id="twitter_card"
                                        name="twitter_card"
                                        value={form.twitter_card}
                                        onChange={handleInputChange}
                                        className="seoSelect"
                                    >
                                        <option value="summary_large_image">summary_large_image (Foto Grande Panorámica - Recomendado)</option>
                                        <option value="summary">summary (Tarjeta Pequeña)</option>
                                    </select>
                                </div>

                                <div className="seoFormGroup">
                                    <label htmlFor="twitter_site">Usuario Oficial de Twitter / X</label>
                                    <input
                                        id="twitter_site"
                                        type="text"
                                        name="twitter_site"
                                        value={form.twitter_site}
                                        onChange={handleInputChange}
                                        placeholder="@seylistours"
                                        className="seoInput"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Columna Derecha: Vista Previa en Redes Sociales */}
                        <div className="seoPreviewCard">
                            <div className="previewCardHeader">
                                <div className="previewCardBadge socialBadge" style={{ background: '#ecfdf5', color: '#047857' }}>
                                    <FontAwesomeIcon icon={faShareAlt} /> Vista Previa en Redes
                                </div>

                                <div className="socialToggleBtns">
                                    <button
                                        type="button"
                                        className={`toggleSocialBtn ${socialPreviewMode === 'whatsapp' ? 'active' : ''}`}
                                        onClick={() => setSocialPreviewMode('whatsapp')}
                                    >
                                        <i className="fa fa-whatsapp"></i> WhatsApp / FB
                                    </button>
                                    <button
                                        type="button"
                                        className={`toggleSocialBtn ${socialPreviewMode === 'twitter' ? 'active' : ''}`}
                                        onClick={() => setSocialPreviewMode('twitter')}
                                    >
                                        <i className="fa fa-twitter"></i> Twitter / X
                                    </button>
                                </div>
                            </div>

                            {/* Vista Previa de WhatsApp / Facebook */}
                            {socialPreviewMode === 'whatsapp' ? (
                                <div className="whatsappChatPreviewBox">
                                    <div className="whatsappChatBubble">
                                        <div className="socialCardContainer">
                                            <div className="socialCardImageWrapper">
                                                <img
                                                    src={imagenMostrada}
                                                    alt="Open Graph preview"
                                                    className="socialCardImg"
                                                    onError={(e) => { e.target.src = '/logo.png'; }}
                                                />
                                            </div>
                                            <div className="socialCardContent">
                                                <span className="socialCardDomain">
                                                    {form.canonical_url ? form.canonical_url.replace(/^https?:\/\//, '').replace(/\/$/, '').toUpperCase() : 'TINGOAVENTURA.COM'}
                                                </span>
                                                <h4 className="socialCardTitle">
                                                    {form.og_title || form.meta_title || `${form.site_name || 'SEYLIS TOURS'} - Agencia de Viajes`}
                                                </h4>
                                                <p className="socialCardDesc">
                                                    {form.og_description || form.meta_description || '¡Reserva tus vacaciones soñadas! Paquetes turísticos completos, tours guiados y vuelos.'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="bubbleFooterTime">10:45 AM ✓✓</div>
                                    </div>
                                    <span className="previewFootnote">
                                        Así lucirá la tarjeta interactiva cuando compartas el enlace de tu agencia o un tour por WhatsApp a tus clientes y grupos.
                                    </span>
                                </div>
                            ) : (
                                /* Vista Previa de Twitter / X */
                                <div className="twitterCardPreviewBox">
                                    <div className="twitterTweetMock">
                                        <div className="tweetAuthorRow">
                                            <img
                                                src={logoPreviewLocal || form.logo || '/logo.png'}
                                                alt="Avatar"
                                                className="tweetAvatar"
                                                onError={(e) => { e.target.src = '/logo.png'; }}
                                            />
                                            <div>
                                                <strong>{form.site_name || 'SEYLIS TOURS'}</strong>
                                                <span className="tweetHandle">{form.twitter_site || '@seylistours'} · Ahora</span>
                                            </div>
                                        </div>

                                        <p className="tweetText">¡Planifica tu próxima aventura con nosotros! ✈️🏖️ Paquetes turísticos, tours guiados y experiencias únicas.</p>

                                        <div className="twitterMediaCard">
                                            <div className="twitterCardImgWrap">
                                                <img
                                                    src={imagenMostrada}
                                                    alt="Twitter preview"
                                                    className="twitterCardImg"
                                                    onError={(e) => { e.target.src = '/logo.png'; }}
                                                />
                                            </div>
                                            <div className="twitterCardBody">
                                                <span className="twitterCardDomain">
                                                    {form.canonical_url ? form.canonical_url.replace(/^https?:\/\//, '').replace(/\/$/, '') : 'tingoaventura.com'}
                                                </span>
                                                <h5 className="twitterCardTitle">
                                                    {form.og_title || form.meta_title || `${form.site_name || 'SEYLIS TOURS'} - Viajes y Turismo`}
                                                </h5>
                                                <p className="twitterCardSnippet">
                                                    {form.og_description || form.meta_description || 'Paquetes vacacionales, tours guiados y vuelos al mejor precio.'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <span className="previewFootnote">
                                        Simulación de la tarjeta con imagen grande en Twitter / X y redes asociadas.
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ======================================================== */}
                {/* TAB: PWA & APP MÓVIL PROGRESIVA                         */}
                {/* ======================================================== */}
                {activeTab === 'pwa' && (
                    <div className="seoTabGrid">
                        {/* Columna Izquierda: Controles y Ajustes de la PWA */}
                        <div className="seoFormCard">
                            <h3 className="sectionSubtitle">
                                <FontAwesomeIcon icon={faMobileAlt} /> Aplicación Web Progresiva (PWA para Celulares y PC)
                            </h3>

                            {/* Tarjeta Principal de Activación con Botón Switch */}
                            <div className="pwaControlMasterCard">
                                <div className="pwaMasterCardHeader">
                                    <div className="pwaMasterIconWrap">
                                        <FontAwesomeIcon icon={faRocket} />
                                    </div>
                                    <div className="pwaMasterTitleWrap">
                                        <h4>Interruptor de Activación PWA</h4>
                                        <p>Activa o desactiva la instalación de la app para todos los visitantes.</p>
                                    </div>
                                    <div className="pwaMasterSwitchWrap">
                                        {/* Botón Switch Toggle Interactivo */}
                                        <button
                                            type="button"
                                            onClick={handleTogglePwaRapido}
                                            className={`pwaBigSwitchBtn ${String(form.pwa_activo) === '1' ? 'active' : ''}`}
                                            role="switch"
                                            aria-checked={String(form.pwa_activo) === '1'}
                                            title="Haz clic para activar o desactivar la PWA"
                                        >
                                            <span className="pwaBigSwitchThumb">
                                                <FontAwesomeIcon icon={String(form.pwa_activo) === '1' ? faToggleOn : faToggleOff} />
                                            </span>
                                            <span className="pwaBigSwitchLabel">
                                                {String(form.pwa_activo) === '1' ? 'ACTIVADO' : 'DESACTIVADO'}
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                {/* Estado explicativo */}
                                <div className={`pwaStatusAlertBox ${String(form.pwa_activo) === '1' ? 'alertSuccess' : 'alertWarning'}`}>
                                    <div className="pwaStatusAlertIcon">
                                        <FontAwesomeIcon icon={String(form.pwa_activo) === '1' ? faCheckCircle : faInfoCircle} />
                                    </div>
                                    <div className="pwaStatusAlertText">
                                        <strong>
                                            {String(form.pwa_activo) === '1'
                                                ? '¡La PWA está actualmente ACTIVA en tu sitio web!'
                                                : 'La PWA está actualmente DESACTIVADA'}
                                        </strong>
                                        <p>
                                            {String(form.pwa_activo) === '1'
                                                ? 'Los clientes que visiten la web desde un teléfono móvil (Android / iPhone) o computadora verán la ventana emergente oficial para instalar la aplicación con el logo de tu agencia.'
                                                : 'Los visitantes no recibirán avisos de instalación y el sitio web funcionará en modo de navegación estándar.'}
                                        </p>
                                    </div>
                                </div>

                                {/* Botones de acción directa */}
                                <div className="pwaMasterActions">
                                    <button
                                        type="button"
                                        onClick={handleTogglePwaRapido}
                                        className={`btnActionTogglePwa ${String(form.pwa_activo) === '1' ? 'btnTurnOff' : 'btnTurnOn'}`}
                                    >
                                        <FontAwesomeIcon icon={String(form.pwa_activo) === '1' ? faToggleOff : faToggleOn} />
                                        <span>{String(form.pwa_activo) === '1' ? 'Desactivar PWA' : 'Activar PWA Ahora'}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleProbarPwaPrompt}
                                        className="btnActionTestPwa"
                                        title="Abre la ventana modal de instalación en tu pantalla para comprobarla"
                                    >
                                        <FontAwesomeIcon icon={faEye} />
                                        <span>Probar Modal de Instalación en Vivo</span>
                                    </button>
                                </div>
                            </div>

                            {/* Personalización de Marca para la App Móvil */}
                            <div className="identityCardBlock" style={{ marginTop: '1.25rem' }}>
                                <div className="identityCardHeader">
                                    <h4>
                                        <FontAwesomeIcon icon={faBrush} style={{ color: '#059669' }} /> Personalización del Banner de Instalación
                                    </h4>
                                    <span className="identityBadgeTag">Textos de la App</span>
                                </div>
                                <p className="identityDescription">
                                    Configura el nombre y el subtítulo que verán los usuarios al recibir la invitación de instalación de tu agencia:
                                </p>

                                <div className="seoFormGroup">
                                    <label htmlFor="pwa_nombre">Nombre de la App / Agencia</label>
                                    <input
                                        id="pwa_nombre"
                                        type="text"
                                        name="pwa_nombre"
                                        value={form.pwa_nombre || ''}
                                        onChange={handleInputChange}
                                        placeholder="Ej: SEYLIS TOURS"
                                        className="seoInput"
                                    />
                                    <span className="seoHelperText">Aparece en el subtítulo y en el nombre del icono en el teléfono del viajero.</span>
                                </div>

                                <div className="seoFormGroup">
                                    <label htmlFor="pwa_descripcion">Mensaje Principal del Modal (Título)</label>
                                    <input
                                        id="pwa_descripcion"
                                        type="text"
                                        name="pwa_descripcion"
                                        value={form.pwa_descripcion || ''}
                                        onChange={handleInputChange}
                                        placeholder="Ej: Instala y Planifica tus Viajes"
                                        className="seoInput"
                                    />
                                    <span className="seoHelperText">Texto atractivo y persuasivo que invita al usuario a descargar tu app.</span>
                                </div>

                                <div className="pwaSaveRow">
                                    <button
                                        type="button"
                                        onClick={handleGuardar}
                                        className="btnPrimarySeo"
                                        style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}
                                        disabled={guardando}
                                    >
                                        <FontAwesomeIcon icon={faSave} />
                                        <span>{guardando ? 'Guardando...' : 'Guardar Textos de la App'}</span>
                                    </button>
                                </div>
                            </div>

                            {/* Beneficios Estratégicos de la PWA */}
                            <div className="pwaBenefitsCard">
                                <h4>
                                    <FontAwesomeIcon icon={faBolt} style={{ color: '#059669', marginRight: 8 }} />
                                    ¿Por qué tener activa la PWA en tu Agencia de Viajes?
                                </h4>
                                <div className="pwaBenefitsGrid">
                                    <div className="pwaBenefitItem">
                                        <div className="pwaBenefitIcon">📱</div>
                                        <div>
                                            <strong>Acceso Directo en el Celular:</strong>
                                            <p>Tu agencia estará en la pantalla de inicio del cliente al lado de WhatsApp e Instagram.</p>
                                        </div>
                                    </div>
                                    <div className="pwaBenefitItem">
                                        <div className="pwaBenefitIcon">⚡</div>
                                        <div>
                                            <strong>Carga Ultrarrápida:</strong>
                                            <p>El Service Worker guarda en caché los recursos principales para que naveguen sin demoras.</p>
                                        </div>
                                    </div>
                                    <div className="pwaBenefitItem">
                                        <div className="pwaBenefitIcon">💰</div>
                                        <div>
                                            <strong>Sin Comisiones de Tiendas:</strong>
                                            <p>Instalable directamente desde el navegador web sin intermediarios de Google Play ni App Store.</p>
                                        </div>
                                    </div>
                                    <div className="pwaBenefitItem">
                                        <div className="pwaBenefitIcon">🔒</div>
                                        <div>
                                            <strong>Seguridad y Confiabilidad:</strong>
                                            <p>Ofrece la misma experiencia segura y moderna que las grandes agencias y aerolíneas internacionales.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Columna Derecha: Simulador Móvil en Vivo y Salud Técnica */}
                        <div className="seoPreviewCard">
                            <h3 className="sectionSubtitle">
                                <FontAwesomeIcon icon={faEye} /> Simulador en Tiempo Real en Celular
                            </h3>
                            <p className="previewBoxIntro">
                                Así experimentan tus clientes el aviso de instalación en su smartphone:
                            </p>

                            {/* Teléfono Mockup con el Modal PWA */}
                            <div className="phoneMockupWrapper">
                                <div className="phoneMockupDevice">
                                    {/* Notch y Altavoz */}
                                    <div className="phoneMockupSpeaker"></div>
                                    <div className="phoneMockupCamera"></div>

                                    {/* Barra de Estado del Móvil */}
                                    <div className="phoneStatusBar">
                                        <span className="phoneStatusTime">9:41</span>
                                        <div className="phoneStatusIcons">
                                            <span>5G</span>
                                            <span>●●●</span>
                                            <span>100%</span>
                                        </div>
                                    </div>

                                    {/* Pantalla Simulada con Contenido de la Agencia */}
                                    <div className="phoneScreenContent">
                                        <div className="phoneScreenHeader" style={{ backgroundColor: form.color_menu_movil || '#059669' }}>
                                            <img
                                                src={logoPreviewLocal || form.logo || '/logo.png'}
                                                alt="Logo Agencia"
                                                className="phoneScreenLogo"
                                                onError={(e) => { e.target.src = '/logo.png'; }}
                                            />
                                            <span className="phoneScreenMenuIcon">☰</span>
                                        </div>

                                        <div className="phoneScreenHero">
                                            <span className="phoneScreenTag">✈️ VIAJA CON NOSOTROS</span>
                                            <h5>{form.site_name || 'SEYLIS TOURS'}</h5>
                                            <p>Los mejores paquetes y tours a tu alcance.</p>
                                        </div>

                                        {/* Modal PWA Flotante dentro del Celular */}
                                        <div className="phonePwaModalSimulation">
                                            <div className="simModalHeader">
                                                <div className="simLogoWrap">
                                                    <img
                                                        src={logoPreviewLocal || form.logo || '/logo.png'}
                                                        alt="Logo App"
                                                        className="simLogoImg"
                                                        onError={(e) => { e.target.src = '/logo.png'; }}
                                                    />
                                                </div>
                                                <div className="simInfoWrap">
                                                    <span className="simBadge">
                                                        <FontAwesomeIcon icon={faCheckCircle} /> App Oficial
                                                    </span>
                                                    <h6>{form.pwa_descripcion || 'Instala y Planifica tus Viajes'}</h6>
                                                    <p>{form.pwa_nombre || form.site_name || 'SEYLIS TOURS'}</p>
                                                </div>
                                            </div>

                                            <div className="simModalActions">
                                                <button
                                                    type="button"
                                                    className="simInstallBtn"
                                                    style={{ backgroundColor: form.color_menu_movil || '#059669' }}
                                                    onClick={handleProbarPwaPrompt}
                                                >
                                                    <FontAwesomeIcon icon={faDownload} /> Instalar Aplicación
                                                </button>
                                                <span className="simDismissLink">Más tarde</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Checklist Técnico de Salud PWA */}
                            <div className="pwaHealthAuditCard">
                                <h5>
                                    <FontAwesomeIcon icon={faShieldAlt} style={{ color: '#059669', marginRight: 8 }} />
                                    Diagnóstico Técnico de la PWA
                                </h5>
                                <div className="pwaAuditList">
                                    <div className="pwaAuditItem">
                                        <div className="auditCheckIcon">✓</div>
                                        <div className="auditCheckText">
                                            <strong>Web App Manifest:</strong>
                                            <span>Configurado en <code>public/manifest.json</code> con soporte standalone y metadatos turísticos.</span>
                                        </div>
                                    </div>
                                    <div className="pwaAuditItem">
                                        <div className="auditCheckIcon">✓</div>
                                        <div className="auditCheckText">
                                            <strong>Service Worker (/sw.js):</strong>
                                            <span>
                                                {String(form.pwa_activo) === '1'
                                                    ? 'Habilitado y listo para interceptar caché y acelerar el sitio.'
                                                    : 'Desactivado temporalmente según la configuración actual.'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="pwaAuditItem">
                                        <div className="auditCheckIcon">✓</div>
                                        <div className="auditCheckText">
                                            <strong>Compatibilidad Multiplataforma:</strong>
                                            <span>Android (Chrome), iOS (Safari - Añadir a inicio), Windows y Mac (Chrome, Edge).</span>
                                        </div>
                                    </div>
                                    <div className="pwaAuditItem">
                                        <div className="auditCheckIcon">✓</div>
                                        <div className="auditCheckText">
                                            <strong>Iconos de Alta Resolución:</strong>
                                            <span>Íconos adaptativos 192x192 y 512x512 vinculados a la identidad visual.</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ======================================================== */}
                {/* TAB 4: CÓDIGO HTML & GUÍA TURÍSTICA                     */}
                {/* ======================================================== */}
                {activeTab === 'code' && (
                    <div className="seoTabGridSingle">
                        <div className="seoFormCard">
                            <div className="sectionHeaderWithAction">
                                <div>
                                    <h3 className="sectionSubtitle" style={{ margin: 0 }}>
                                        <FontAwesomeIcon icon={faCode} /> Etiquetas HTML Generadas en Tiempo Real
                                    </h3>
                                    <p className="sectionHint">
                                        Estas son las etiquetas estándar que se inyectan automáticamente en el encabezado (`&lt;head&gt;`) de la web para Google, redes sociales y buscadores.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={copiarCodigoPortapapeles}
                                    className="btnPrimarySeo"
                                    style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                    <span>Copiar Código</span>
                                </button>
                            </div>

                            <div className="codeViewerWrapper">
                                <pre className="codePreBlock">
                                    <code>{codigoGenerado}</code>
                                </pre>
                            </div>

                            <div className="seoGuidelinesBox">
                                <h4>
                                    <FontAwesomeIcon icon={faCompass} style={{ color: '#059669', marginRight: 8 }} />
                                    5 Recomendaciones Estratégicas para Posicionar tu Agencia de Viajes
                                </h4>
                                <div className="guidelinesGrid">
                                    <div className="guidelineItem">
                                        <strong>1. Título Enfocado en el Destino + Tipo de Servicio:</strong>
                                        <p>Ejemplo: "Tours en Tingo María & Selva Central | SEYLIS TOURS - Paquetes Turísticos". Incluye el nombre del destino y los servicios principales al inicio.</p>
                                    </div>
                                    <div className="guidelineItem">
                                        <strong>2. Descripciones con Llamada a la Acción (CTA):</strong>
                                        <p>Agrega verbos directos como "Reserva hoy", "Cotiza en minutos", o "Descubre paquetes con traslados y guías incluidos" para maximizar las consultas.</p>
                                    </div>
                                    <div className="guidelineItem">
                                        <strong>3. Imagen Open Graph Panorámica e Impactante:</strong>
                                        <p>Las fotos de paisajes reales (cataratas cristalinas, lagunas, miradores o grupos de turistas sonrientes) multiplican los clics en WhatsApp y redes sociales.</p>
                                    </div>
                                    <div className="guidelineItem">
                                        <strong>4. Palabras Clave Locales y Específicas:</strong>
                                        <p>Complementa términos generales ("agencia de viajes") con términos de alta intención de compra ("tours cueva de las lechuzas", "paquetes todo incluido", "full day selva").</p>
                                    </div>
                                    <div className="guidelineItem">
                                        <strong>5. Ficha Oficial y Seguridad al Pasajero:</strong>
                                        <p>Mostrar claramente la Licencia de Turismo (DIRCETUR/MINCETUR), número de WhatsApp y horarios brinda la confianza decisiva para cerrar reservas en línea.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
